import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Upload, FileText, Download, Trash2, Eye, EyeOff } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { useConfirm } from "./ConfirmProvider";

const CATEGORIES = [
  { key: "quotation", label: "Quotation", bg: "#DBEAFE", fg: "#1E40AF" },
  { key: "permit", label: "Permit", bg: "#FFEDD5", fg: "#C2410C" },
  { key: "invoice", label: "Invoice", bg: "#DCFCE7", fg: "#166534" },
  { key: "contract", label: "Contract", bg: "#FEE2E2", fg: "#9F1239" },
  { key: "design", label: "Design", bg: "#FFE4D6", fg: "#E07A5F" },
  { key: "other", label: "Other", bg: "#F7F4EC", fg: "#7A7571" },
];

const catMeta = (k) => CATEGORIES.find((c) => c.key === k) || CATEGORIES[5];

function fmtBytes(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function DocumentsTab({ projectId, canManage, isCustomer }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  // Firm-side users only — homeowners don't need to see the limit gauge.
  const [docLimit, setDocLimit] = useState(null);
  const confirm = useConfirm();

  const load = async () => {
    const { data } = await api.get(`/projects/${projectId}/documents`);
    setDocs(data.documents || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, [projectId]);

  // Fetch the firm's effective per-project document limit so the tab can
  // show N used / N max and warn as the project approaches the cap.
  useEffect(() => {
    if (isCustomer) return;
    let cancelled = false;
    api.get("/workspace/subscription")
      .then((r) => {
        if (cancelled) return;
        setDocLimit(r.data?.effective_limits?.max_documents_per_project ?? null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isCustomer]);

  const download = async (d) => {
    try {
      const resp = await api.get(`/documents/${d.document_id}/download`, {
        responseType: "blob",
      });
      const blob = new Blob([resp.data], {
        type: d.content_type || "application/octet-stream",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.file_name || d.name || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (d) => {
    const ok = await confirm({
      title: `Delete "${d.name}"?`,
      message: "This can't be undone. The customer will lose access.",
      confirmText: "Delete", danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/documents/${d.document_id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const used = docs.length;
  const hasLimit = docLimit != null && Number.isFinite(Number(docLimit));
  const pct = hasLimit && docLimit > 0 ? Math.min(100, Math.round((used / docLimit) * 100)) : 0;
  const nearCap = hasLimit && pct >= 80 && pct < 100;
  const atCap = hasLimit && used >= docLimit;
  const barColor = atCap ? "bg-[#9F1239]" : nearCap ? "bg-[#E07A5F]" : "bg-[#81B29A]";

  return (
    <div>
      {/* Counter + limit gauge — firm-side only. Homeowners see nothing
          about the tier limit. */}
      {!isCustomer && (
        <div
          className="rounded-2xl bg-[#F7F4EC] p-3 mb-4 flex flex-col sm:flex-row sm:items-center gap-3"
          data-testid="documents-counter"
        >
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571]">Documents in this project</div>
            <div
              className={`text-lg font-display font-bold mt-0.5 ${atCap ? "text-[#9F1239]" : "text-[#2C2A29]"}`}
              data-testid="documents-count"
            >
              {used.toLocaleString()}{hasLimit && <span className="text-[#7A7571] font-normal"> / {Number(docLimit).toLocaleString()}</span>}
            </div>
            {hasLimit && (
              <div className="mt-2 h-1.5 rounded-full bg-white overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${barColor}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            )}
          </div>
          {atCap && (
            <div className="text-xs text-[#9F1239] font-semibold sm:text-right" data-testid="documents-at-cap">
              You've reached the plan's per-project document limit. Add the Documents pack add-on to lift it.
            </div>
          )}
          {nearCap && !atCap && (
            <div className="text-xs text-[#B85536] font-semibold sm:text-right" data-testid="documents-near-cap">
              You're near the per-project document limit.
            </div>
          )}
        </div>
      )}
      {canManage && (
        <div className="flex items-center justify-end mb-4">
          <button data-testid="upload-doc-btn" onClick={() => setUploadOpen(true)}
            className="btn-primary inline-flex items-center gap-2"
            disabled={atCap}
            title={atCap ? "Document limit reached" : undefined}
          >
            <Upload size={16} /> Upload document
          </button>
        </div>
      )}

      {loading && <p className="text-[#7A7571]">Loading…</p>}
      {!loading && docs.length === 0 && (
        <div className="card-soft text-center py-12 text-[#7A7571]">
          {isCustomer ? "Your ID firm hasn't uploaded any documents yet." : "No documents yet — upload the first one."}
        </div>
      )}

      <div className="space-y-3">
        {docs.map((d) => {
          const meta = catMeta(d.category);
          return (
            <div key={d.document_id} className="card-soft flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`doc-${d.document_id}`}>
              <div className="flex items-start gap-3 min-w-0 flex-1">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ background: meta.bg, color: meta.fg }}>
                  <FileText size={20} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display text-lg font-semibold text-[#2C2A29] truncate">{d.name}</div>
                    <span className="badge" style={{ background: meta.bg, color: meta.fg }}>{meta.label}</span>
                    {!d.visible_to_customer && !isCustomer && (
                      <span className="badge badge-neutral inline-flex items-center gap-1">
                        <EyeOff size={11} /> Staff only
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#7A7571] mt-1 truncate">
                    {d.file_name} · {fmtBytes(d.size_bytes)}
                  </div>
                  <div className="text-xs text-[#7A7571] mt-0.5">
                    Uploaded by {d.uploaded_by_name} · {new Date(d.uploaded_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => download(d)} data-testid={`doc-download-${d.document_id}`}
                  className="btn-secondary text-sm !py-2 !px-4 inline-flex items-center gap-1">
                  <Download size={14} /> Download
                </button>
                {canManage && (
                  <button onClick={() => del(d)} data-testid={`doc-delete-${d.document_id}`}
                    className="btn-ghost !p-2 text-[#9F1239]" title="Delete"><Trash2 size={14} /></button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} projectId={projectId} onDone={load} />
    </div>
  );
}

function UploadDialog({ open, onOpenChange, projectId, onDone }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("quotation");
  const [visibleToCustomer, setVisibleToCustomer] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setFile(null); setName(""); setCategory("quotation"); setVisibleToCustomer(true); if (fileRef.current) fileRef.current.value = ""; }
  }, [open]);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 25_000_000) { toast.error("Max 25MB"); return; }
    setFile(f);
    if (!name) setName(f.name.replace(/\.[^.]+$/, ""));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please choose a file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      fd.append("name", name || file.name);
      fd.append("category", category);
      fd.append("visible_to_customer", visibleToCustomer ? "true" : "false");
      await api.post(`/projects/${projectId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      onOpenChange(false);
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[800px] w-[95vw] rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Upload document</DialogTitle>
          <DialogDescription>PDFs, images, and Office files up to 25MB.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">File</label>
            <input ref={fileRef} type="file" onChange={pick} data-testid="doc-file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.heic"
              className="block w-full text-sm text-[#7A7571] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#F7F4EC] file:text-[#2C2A29] hover:file:bg-[#EBE5D9]" />
            {file && <div className="text-xs text-[#7A7571] mt-2">{file.name} · {fmtBytes(file.size)}</div>}
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">Display name</label>
            <input data-testid="doc-name" className="input-soft" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Reno permit — HDB Tanglin Halt" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">Category</label>
            <div className="grid grid-cols-3 gap-2">
              {CATEGORIES.map((c) => (
                <button key={c.key} type="button" onClick={() => setCategory(c.key)}
                  data-testid={`doc-cat-${c.key}`}
                  className={`px-3 py-2 rounded-2xl border text-sm font-semibold ${category === c.key ? "border-[#E07A5F] bg-[#FFF6F1]" : "border-[#EBE5D9] bg-white text-[#7A7571]"}`}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-start gap-3 p-3 rounded-2xl bg-[#F7F4EC] cursor-pointer">
            <input type="checkbox" data-testid="doc-visible" className="mt-1"
              checked={visibleToCustomer} onChange={(e) => setVisibleToCustomer(e.target.checked)} />
            <span className="text-sm text-[#2C2A29]">
              <b>Visible to customer</b> — untick to keep this document internal to your firm.
            </span>
          </label>
          <button data-testid="doc-submit" disabled={busy || !file} className="btn-primary w-full inline-flex items-center justify-center gap-2">
            <Upload size={16} /> {busy ? "Uploading…" : "Upload document"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
